import React, { useEffect, useState } from 'react';
import { RouteComponentProps } from 'react-router-dom';
import * as publicIp from 'public-ip';

import {
  MuiThemeProvider,
  CssBaseline,
  makeStyles,
  createStyles,
  useTheme,
  useMediaQuery,
  AppBar,
  Toolbar,
  Container,
  Grid,
  Typography,
  Divider,
  IconButton,
  SvgIcon,
} from '@material-ui/core';
import WebsiteIcon from '@material-ui/icons/Language';

import {
  defaultTheme,
  generateTheme,
  StartupCard,
  CtaButton,
} from '@antlerengineering/components';
import AntlerLogo from '@antlerengineering/components/src/assets/antler-logo.svg';

import { db } from '../firebase';
import useDoc from 'hooks/useDoc';
import { makeId } from '../util';

const altTheme = generateTheme({
  palette: {
    background: {
      default: '#FFF',
      paper: '#FAFAFA',
    },
  } as any,
  typography: {
    h3: { fontWeight: 'normal' },
    button: {
      textTransform: 'none',
      letterSpacing: 'normal',
    },
  },
  overrides: {
    MuiToolbar: {
      regular: {
        [defaultTheme.breakpoints.up('sm')]: { minHeight: 88 },
      },
    },
    MuiIconButton: {
      root: {
        color: defaultTheme.palette.text.disabled,
        transition: defaultTheme.transitions.create(
          ['color', 'background-color'],
          {
            duration: defaultTheme.transitions.duration.short,
          }
        ),
        '&:hover': { color: defaultTheme.palette.secondary.main },
      },
    },
  },
});

const useStyles = makeStyles(theme =>
  createStyles({
    appBarContainer: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
    },
    allCompaniesButton: {
      paddingLeft: theme.spacing(2),
      marginRight: theme.spacing(-2),
    },

    main: {
      marginTop: theme.spacing(5),
      marginBottom: theme.spacing(5),
      cursor: 'default',
    },
    description: {
      whiteSpace: 'pre-line',
      maxWidth: '70ch',
    },

    grid: {
      marginTop: theme.spacing(3),
    },
    gridItem: {
      minWidth: 260,
      maxWidth: 400,
      minHeight: 300,
    },

    actionsGrid: {
      padding: theme.spacing(0, 0.5),
    },
    buttonCrunchbase: {
      '&:hover': { color: '#0a4063' },
    },
    buttonAngelList: {
      '&:hover': { color: '#1a1919' },
    },
  })
);

export default function PortfolioShortlistView({
  match,
}: RouteComponentProps<any>) {
  const classes = useStyles();
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down('xs'));

  const { id } = match.params;

  const [docState] = useDoc({ path: `portfolioShortlists/${id}` });

  const [sessionDocPath, setSessionDocPath] = useState<string | undefined>();
  const createSession = async (doc: any) => {
    const _viewerIp = await publicIp.v4();
    if (_viewerIp !== doc.creatorIp) {
      const _sessionId = makeId(20);
      const _sessionDocPath = `portfolioShortlists/${id}/sessions/${_sessionId}`;
      await db.doc(`portfolioShortlists/${id}/sessions/${_sessionId}`).set({
        createdAt: new Date(),
        viewerIp: _viewerIp,
        shortListId: id,
      });
      setSessionDocPath(_sessionDocPath);
    }
  };

  useEffect(() => {
    if (!docState.loading && docState.doc && !sessionDocPath)
      createSession(docState.doc);
  }, [docState.loading]);

  if (!docState.doc) return null;

  return (
    <MuiThemeProvider theme={altTheme}>
      <CssBaseline />
      <AppBar position="fixed" color="inherit" elevation={0}>
        <Toolbar component={Container} className={classes.appBarContainer}>
          <a href="https://antler.co/">
            <img
              src={AntlerLogo}
              alt="Antler"
              width={124}
              height={34}
              style={{ display: 'block' }}
            />
          </a>

          <CtaButton
            variant="text"
            component="a"
            href="https://antler.co/our-startups"
            className={classes.allCompaniesButton}
          >
            {isXs ? 'All portfolio' : 'All portfolio companies'}
          </CtaButton>
        </Toolbar>
      </AppBar>

      <Container className={classes.main} component="main">
        <Toolbar />

        <Typography variant="h3" component="h1" gutterBottom>
          {docState.doc.header}
        </Typography>
        <Typography
          variant="body1"
          color="textSecondary"
          className={classes.description}
        >
          {docState.doc.description}
        </Typography>

        <Grid container className={classes.grid} spacing={4}>
          {docState.doc.shortListedCompanies.map((company: any) => (
            <Grid
              item
              xs={12}
              sm={6}
              md={4}
              lg={3}
              className={classes.gridItem}
            >
              <StartupCard
                {...company}
                CardProps={{
                  href: `https://antler.co/our-startups/${encodeURIComponent(
                    company.teamName.toLowerCase()
                  )}`,
                  onClick: e => {
                    if (sessionDocPath) {
                      e.preventDefault();

                      db.doc(sessionDocPath)
                        .get()
                        .then((doc: any) => {
                          const clicks = doc.data()['clicks'] ?? [];
                          doc.ref.update({
                            clicks: [
                              ...clicks,
                              {
                                clickedAt: new Date(),
                                type: 'onePager',
                                teamName: company.teamName,
                              },
                            ],
                          });
                        });

                      window.open(
                        `https://antler.co/our-startups/${company.teamName}`,
                        '_blank'
                      );
                    }
                  },
                }}
                actions={
                  (company.website ||
                    company.crunchbase ||
                    company.angelList) && (
                    <>
                      <Divider variant="middle" />

                      <Grid
                        container
                        className={classes.actionsGrid}
                        onClick={e => e.stopPropagation()}
                      >
                        {company.website && (
                          <Grid item>
                            <IconButton
                              component="a"
                              href={company.website}
                              target="_blank"
                              rel="noopener"
                              aria-label="Open startup website"
                            >
                              <WebsiteIcon />
                            </IconButton>
                          </Grid>
                        )}
                        <Grid item xs />
                        {company.crunchbase && (
                          <Grid item>
                            <IconButton
                              component="a"
                              href={company.crunchbase}
                              target="_blank"
                              rel="noopener"
                              aria-label="Crunchbase"
                              className={classes.buttonCrunchbase}
                            >
                              <SvgIcon>
                                <path d="M21 0a3 3 0 013 3v18a3 3 0 01-3 3H3a3 3 0 01-3-3V3a3 3 0 013-3zM6.575 9.029C4.535 9.269 3 10.975 3 13c0 2.025 1.535 3.73 3.575 3.971 2.04.241 3.94-1.059 4.425-3.027H9.17a2.336 2.336 0 01-2.627 1.261 2.3 2.3 0 01-1.804-2.266 2.302 2.302 0 011.867-2.216 2.334 2.334 0 012.59 1.333H11c-.485-1.968-2.385-3.268-4.425-3.027zM14.636 5H13v11.685h1.646v-.426l.18.121c1.408.89 3.222.82 4.56-.197a4.106 4.106 0 001.397-4.582 4.038 4.038 0 00-3.877-2.76h-.296l-.235.024a3.956 3.956 0 00-1.739.678V5zm2.178 5.62l.119.002a2.332 2.332 0 012.286 2.277c.008.104.008.208 0 .313v.072l-.027.144-.036.142c-.03.1-.067.2-.112.294v.036l-.084.161c-.329.575-.886.985-1.534 1.123l-.22.036h-.049l-.126.004-.125-.004c-.105 0-.21-.008-.314-.023h-.072l-.2-.042a2.275 2.275 0 01-.388-.139h-.044l-.184-.102a2.325 2.325 0 01-.336-.251 2.357 2.357 0 01-.408-.495 2.349 2.349 0 01-.12-2.217 2.32 2.32 0 011.855-1.329l.119-.002z" />
                              </SvgIcon>
                            </IconButton>
                          </Grid>
                        )}
                        {company.angelList && (
                          <Grid item>
                            <IconButton
                              component="a"
                              href={company.angelList}
                              target="_blank"
                              rel="noopener"
                              aria-label="AngelList"
                              className={classes.buttonAngelList}
                            >
                              <SvgIcon>
                                <path d="M16.8045232,9.93632877 C17.5666824,7.94906301 18.1002069,6.26755068 18.5574762,4.96813151 C18.9385558,3.66877808 19.167256,2.90446027 19.167256,2.59870685 C19.167256,2.29301918 19.0910007,2.06367123 18.9385558,1.83438904 C18.7861764,1.68151233 18.6337314,1.60510685 18.4050312,1.60510685 C18.1002069,1.60510685 17.795317,1.83438904 17.4904271,2.36942466 C17.1856028,2.90446027 16.8045232,3.66877808 16.4234436,4.81532055 L14.7466148,9.55416986 L16.8045232,9.93632877 Z M14.5941698,14.3694247 C14.1369005,14.3694247 13.6795657,14.2930192 13.2222964,14.2165479 C12.7649616,14.1401425 12.383882,14.0636712 12.0028024,13.9108603 C12.1552474,14.2930192 12.3076923,14.5987068 12.4601373,14.9808658 C12.6125822,15.3630247 12.6887719,15.6687781 12.7649616,16.050937 C12.9936618,15.7452493 13.2984861,15.4394959 13.603376,15.1337425 C13.9844556,14.8280548 14.2893455,14.5987068 14.5941698,14.3694247 Z M12.7649616,9.40129315 L10.9357533,4.28028493 C10.4784841,2.98086575 10.1735942,2.06367123 9.86870428,1.68151233 C9.64006965,1.29935342 9.41143501,1.07007123 9.10654512,1.07007123 C8.87791048,1.07007123 8.72546553,1.14647671 8.57302058,1.29935342 C8.34438595,1.52863562 8.26813069,1.75798356 8.26813069,2.06367123 C8.26813069,2.59870685 8.49683089,3.43949589 8.87791048,4.66244384 C9.25899006,5.88532603 9.86870428,7.56690411 10.630929,9.55416986 C10.7071187,9.40129315 10.7833084,9.32482192 10.9357533,9.32482192 C11.0881983,9.24841644 11.2406432,9.24841644 11.3930882,9.24841644 L11.7741678,9.24841644 C12.0028024,9.32482192 12.3076923,9.32482192 12.7649616,9.40129315 Z M11.1644536,17.3502904 C11.3168329,17.3502904 11.5455331,17.2738849 11.6979125,17.1210082 C11.8503575,16.9681315 11.9266127,16.7388493 11.9266127,16.5859726 C11.9266127,16.3566904 11.7741678,15.8980603 11.4692779,15.1337425 C11.1644536,14.3694247 10.7833084,13.6051068 10.3260391,12.9171945 C10.0211492,12.3821589 9.64006965,11.9235945 9.33517975,11.6942466 C9.03035543,11.3885589 8.72546553,11.3120877 8.42057564,11.3120877 C8.191941,11.3120877 7.96330636,11.4649644 7.65841647,11.7707178 C7.35352657,12.0764055 7.27733688,12.3057534 7.27733688,12.6114411 C7.27733688,12.840789 7.42978183,13.2993534 7.65841647,13.834389 C7.96330636,14.3694247 8.26813069,14.9044603 8.72546553,15.5159014 C9.18280037,16.1274082 9.64006965,16.6624438 10.0974045,17.0446027 C10.5546738,17.1974795 10.8595637,17.3502904 11.1644536,17.3502904 Z M6.21028782,16.9681315 C6.36273277,17.1210082 6.5913674,17.4267616 6.82006761,17.8089205 C7.50597152,18.8025205 8.191941,19.3375562 8.80165522,19.3375562 C9.03035543,19.3375562 9.18280037,19.2611507 9.33517975,19.108274 C9.4876247,18.9553973 9.56387996,18.8025205 9.56387996,18.7261151 C9.56387996,18.5732384 9.4876247,18.2674849 9.25899006,17.885326 C9.03035543,17.5031671 8.72546553,17.0446027 8.34438595,16.5859726 C7.8870511,16.050937 7.50597152,15.5923726 7.27733688,15.3630247 C6.97244699,15.1337425 6.74381235,14.9808658 6.5913674,14.9808658 C6.21028782,14.9808658 5.82920823,15.2102137 5.52431834,15.5923726 C5.21949401,15.9745315 5.06704906,16.5095671 5.06704906,17.0446027 C5.06704906,17.5031671 5.14323875,17.9617973 5.37193896,18.5732384 C5.6005736,19.108274 5.90546349,19.7197151 6.36273277,20.2547507 C7.04870224,21.0191342 7.81086141,21.7069808 8.80165522,22.165611 C9.79251459,22.6241753 10.7833084,22.8535233 12.0028024,22.8535233 C14.1369005,22.8535233 15.8899191,22.0892055 17.3380477,20.4840986 C18.7861764,18.8789918 19.4720803,16.891726 19.4720803,14.4458301 C19.4720803,13.6815123 19.3958906,13.1464767 19.3196353,12.6879123 C19.2434457,12.2292822 19.014811,11.9235945 18.7861764,11.7707178 C18.3288415,11.3885589 17.4904271,11.0828055 16.1947434,10.7770521 C14.8990597,10.4713644 13.5271863,10.3184877 12.1552474,10.3184877 C11.7741678,10.3184877 11.4692779,10.3948932 11.3168329,10.5477699 C11.1644536,10.7006466 11.0881983,10.9299288 11.0881983,11.2356822 C11.0881983,12 11.4692779,12.5350356 12.3076923,12.840789 C13.1460412,13.1464767 14.5179801,13.3758247 16.3471884,13.3758247 L17.0331578,13.3758247 C17.1856028,13.3758247 17.3380477,13.4522301 17.4142374,13.5286356 C17.4904271,13.6815123 17.5666824,13.834389 17.5666824,14.0636712 C17.4142374,14.2165479 17.0331578,14.4458301 16.4234436,14.6751781 C15.8136638,14.9044603 15.4325842,15.1337425 15.1276943,15.3630247 C14.4417904,15.8216548 13.9082659,16.4330959 13.5271863,17.1210082 C13.1460412,17.8089205 12.9174065,18.4203616 12.9174065,19.0318685 C12.9174065,19.4140274 12.9936618,19.7961863 13.1460412,20.3312219 C13.2984861,20.8662575 13.3747414,21.1719452 13.3747414,21.2484164 L13.3747414,21.5541699 C12.9174065,21.5541699 12.5363269,21.2484164 12.2314371,20.7133808 C11.9266127,20.1783452 11.8503575,19.4904329 11.8503575,18.5732384 L11.8503575,18.4203616 C11.7741678,18.4968329 11.6979125,18.5732384 11.6217228,18.5732384 C11.5455331,18.5732384 11.4692779,18.6497096 11.3168329,18.6497096 L11.0120086,18.6497096 C10.9357533,18.6497096 10.8595637,18.5732384 10.7071187,18.5732384 C10.7071187,18.7261151 10.7833084,18.8025205 10.7833084,18.9553973 L10.7833084,19.2611507 C10.7833084,19.6433096 10.630929,20.0254685 10.3260391,20.3312219 C10.0211492,20.6369096 9.64006965,20.7897863 9.18280037,20.7897863 C8.49683089,20.7897863 7.81086141,20.4840986 7.04870224,19.7961863 C6.36273277,19.108274 5.98165318,18.4968329 5.98165318,17.8089205 C5.98165318,17.6560438 5.98165318,17.5796384 6.05784287,17.5031671 C6.05784287,17.1210082 6.13409813,17.0446027 6.21028782,16.9681315 Z M17.947762,10.2420164 C18.9385558,10.3948932 19.6245252,10.8535233 20.0056048,11.4649644 C20.3866844,12.0764055 20.6153846,13.1464767 20.6153846,14.5223014 C20.6153846,17.2738849 19.7769702,19.5669041 18.1002069,21.3248219 C16.4234436,23.0828055 14.2893455,24 11.6979125,24 C10.7071187,24 9.71632491,23.8471233 8.72546553,23.4649644 C7.73467172,23.0828055 6.97244699,22.5477699 6.28654308,21.9363288 C5.52431834,21.2484164 4.91460412,20.4076274 4.53352453,19.6433096 C4.22863464,18.8025205 4,17.9617973 4,17.1210082 C4,16.2038137 4.22863464,15.4394959 4.60971422,14.9044603 C4.99079381,14.3694247 5.67676329,14.0636712 6.51517771,13.834389 C6.36273277,13.4522301 6.21028782,13.1464767 6.13409813,12.840789 C6.05784287,12.6114411 5.98165318,12.3821589 5.98165318,12.2292822 C5.98165318,11.7707178 6.21028782,11.2356822 6.74381235,10.7006466 C7.27733688,10.165611 7.73467172,9.93632877 8.191941,9.93632877 C8.42057564,9.93632877 8.57302058,9.93632877 8.80165522,10.0127342 C9.03035543,10.0891397 9.25899006,10.165611 9.56387996,10.3948932 C8.87791048,8.10193973 8.191941,6.19107945 7.81086141,4.89172603 C7.42978183,3.5923726 7.27733688,2.75158356 7.27733688,2.21654795 C7.27733688,1.52863562 7.42978183,0.993665753 7.81086141,0.611441096 C8.11575131,0.229282192 8.64927584,0 9.25899006,0 C10.2497839,0 11.5455331,2.29301918 13.1460412,6.95539726 C13.4509311,7.71971507 13.603376,8.33122192 13.755821,8.7897863 C13.9082659,8.48409863 14.0606453,7.94906301 14.2893455,7.33755616 C15.8899191,2.75158356 17.2617925,0.458630137 18.3288415,0.458630137 C18.8623661,0.458630137 19.3196353,0.611441096 19.7007805,0.993665753 C20.0056048,1.37582466 20.234305,1.91079452 20.234305,2.52230137 C20.234305,2.98086575 20.0818601,3.89806027 19.7007805,5.19747945 C19.2434457,6.49683288 18.7099211,8.17834521 17.947762,10.2420164 Z" />
                              </SvgIcon>
                            </IconButton>
                          </Grid>
                        )}
                      </Grid>
                    </>
                  )
                }
              />
            </Grid>
          ))}
        </Grid>
      </Container>
    </MuiThemeProvider>
  );
}
